import {
  DEFAULT_PLAYBACK_RATE,
  PLAYBACK_RATE_OPTIONS,
  VIDEO_PROCESSING_POLL_INITIAL_MS,
  VIDEO_PROCESSING_POLL_MAX_ELAPSED_MS,
  VIDEO_PROCESSING_POLL_MAX_MS,
} from "../constants/meeting-details";
import { SHORT_MEETING_THRESHOLD_SECONDS } from "../constants/meetings";
import { languages } from "../i18n/languages";
import {
  EMediaAvailability,
  type IFormatSecondsToDurationLabelProps,
  type IFormatTimeRangeProps,
  type IGetDurationDisplayProps,
  type IGetLanguageDisplayNameParams,
  type IGetShouldShowShortMeetingMessageProps,
  type IGroupedNotesByThemeItem,
  type ILegacyActionItemsSection,
  type IParseStructuredNotesParams,
  type ISimpleActionItem,
  type IStartVideoProcessingPollParams,
  type IStructuredNote,
  type ITimeRangeEnds,
} from "../types/meeting-details";
import { EMeetingStatus } from "../types/meetings";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const parseStructuredNote = (value: unknown): IStructuredNote | null => {
  if (!isRecord(value)) {
    return null;
  }

  const timeStart = value.time_start;
  const timeEnd = value.time_end;
  const theme = value.theme;
  const emoji = value.emoji;
  const points = value.points;

  if (
    typeof timeStart !== "string" ||
    typeof timeEnd !== "string" ||
    typeof theme !== "string" ||
    !Array.isArray(points)
  ) {
    return null;
  }

  const parsedPoints = points.filter((point): point is string => {
    return typeof point === "string";
  });

  return {
    time_start: timeStart,
    time_end: timeEnd,
    theme,
    points: parsedPoints,
    ...(typeof emoji === "string" ? { emoji } : {}),
  };
};

const parseStructuredNotesArray = (
  value: unknown,
): IStructuredNote[] | null => {
  if (!Array.isArray(value)) {
    return null;
  }

  return value.flatMap((entry) => {
    const parsed = parseStructuredNote(entry);
    return parsed ? [parsed] : [];
  });
};

export const getEmojiForActionItem = (index: number): string => {
  if (index === 0) {
    return "📦";
  }

  if (index === 1) {
    return "🛡️";
  }

  return "🚀";
};

export const formatSecondsToMmSs = (value: number): string => {
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

// NOTE: Renders meeting durations for the Call Assistant list/detail UI.
// Uses mm:ss for sub-hour meetings (e.g. "00:05", "01:32") and
// hh:mm:ss once a meeting reaches an hour (e.g. "01:15:00") so multi-hour
// calls don't surface as "75:00".
export const formatSecondsToDurationLabel = ({
  seconds,
}: IFormatSecondsToDurationLabelProps): string => {
  const safeSeconds =
    Number.isFinite(seconds) && seconds >= 0 ? Math.floor(seconds) : 0;
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;

  const paddedMinutes = minutes.toString().padStart(2, "0");
  const paddedSeconds = remainingSeconds.toString().padStart(2, "0");

  if (hours === 0) {
    return `${paddedMinutes}:${paddedSeconds}`;
  }

  const paddedHours = hours.toString().padStart(2, "0");
  return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
};

// NOTE: Returns the formatted duration label when the backend reports a
// non-zero secondConsumed; null otherwise so callers can hide the slot
// entirely instead of rendering "00:00".
export const getDurationDisplay = ({
  secondsConsumed,
}: IGetDurationDisplayProps): string | null => {
  if (!Number.isFinite(secondsConsumed) || secondsConsumed <= 0) {
    return null;
  }
  return formatSecondsToDurationLabel({ seconds: secondsConsumed });
};

const isMeetingNotesEmpty = (notes: unknown): boolean => {
  if (notes === null || notes === undefined) {
    return true;
  }
  if (typeof notes === "string") {
    return notes.trim().length === 0;
  }
  if (Array.isArray(notes)) {
    return notes.length === 0;
  }
  if (isRecord(notes)) {
    // NOTE: parseStructuredNotes unwraps `{ notes: [...] }` payloads, so an
    // empty array nested inside that wrapper is still no usable notes — recurse
    // to keep the short-meeting heuristic aligned with what the UI renders.
    if ("notes" in notes) {
      return isMeetingNotesEmpty(notes.notes);
    }
    return Object.keys(notes).length === 0;
  }
  return false;
};

// NOTE: Frontend heuristic for "this meeting was too short to transcribe".
// Only fires for PROCESSED meetings so we don't compete with the
// failed/cancelled banners. Threshold lives in
// constants/meetings.ts (SHORT_MEETING_THRESHOLD_SECONDS).
export const getShouldShowShortMeetingMessage = ({
  status,
  secondsConsumed,
  notes,
  transcript,
}: IGetShouldShowShortMeetingMessageProps): boolean => {
  if (status !== EMeetingStatus.PROCESSED) {
    return false;
  }
  if (!Number.isFinite(secondsConsumed)) {
    return false;
  }
  if (secondsConsumed >= SHORT_MEETING_THRESHOLD_SECONDS) {
    return false;
  }
  const hasTranscript =
    typeof transcript === "string" && transcript.trim().length > 0;
  if (hasTranscript) {
    return false;
  }
  return isMeetingNotesEmpty(notes);
};

export const normalizeStartTimeLabel = (start: number | string): string => {
  if (typeof start === "number") {
    if (!Number.isFinite(start) || start < 0) {
      return "00:00";
    }
    return formatSecondsToMmSs(start);
  }

  if (start.includes(":")) {
    return start;
  }

  const seconds = Number.parseFloat(start);
  if (!Number.isFinite(seconds) || seconds < 0) {
    return "00:00";
  }

  return formatSecondsToMmSs(seconds);
};

export const buildSearchRegex = (query: string): RegExp | null => {
  const trimmed = query.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return new RegExp(String.raw`(${trimmed})`, "gi");
  } catch {
    return null;
  }
};

export const secondsToTime = (seconds: number): string => {
  const safeSeconds = Number.isFinite(seconds) && seconds >= 0 ? seconds : 0;
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = Math.floor(safeSeconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
    .toString()
    .padStart(2, "0")}`;
};

export const timeToSeconds = (timeStr: string): number => {
  const normalizedTime = timeStr.trim();
  if (!normalizedTime) {
    return 0;
  }

  if (!normalizedTime.includes(":")) {
    const parsed = Number.parseFloat(normalizedTime);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  }

  const parts = normalizedTime
    .split(":")
    .map((value) => Number.parseFloat(value));

  if (parts.some((part) => Number.isNaN(part))) {
    return 0;
  }

  if (parts.length === 2) {
    const [minutes, seconds] = parts;
    return Math.max(0, (minutes || 0) * 60 + (seconds || 0));
  }

  if (parts.length === 3) {
    const [hours, minutes, seconds] = parts;
    return Math.max(
      0,
      (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0),
    );
  }

  return 0;
};

export const getLanguageDisplayName = ({
  language,
  t,
}: IGetLanguageDisplayNameParams): string => {
  if (!language) {
    return "";
  }

  // NOTE: meeting.language may arrive either as a locale code ("ru") or as a
  // full English name ("russian"). Normalize both to the locale code so the
  // label resolves through the active interface language's translations.
  const normalizedLanguage = language.trim().toLowerCase();
  const matchedLanguage = languages.find(
    (supportedLanguage) => supportedLanguage.key === normalizedLanguage,
  );
  const languageCode = matchedLanguage
    ? matchedLanguage.code
    : normalizedLanguage;

  const translationKey = `details.main_content.languages.${languageCode}`;
  const translatedLabel = t(translationKey);
  if (translatedLabel !== translationKey) {
    return translatedLabel;
  }

  return language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
};

export const convertTimeFormat = (time: string): string => {
  const parts = time.split(":").map((value) => Number.parseFloat(value));
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    return time;
  }

  const [hours, minutes, seconds] = parts;
  const totalMinutes = (hours || 0) * 60 + (minutes || 0);
  return `${totalMinutes}:${(seconds || 0).toString().padStart(2, "0")}`;
};

export const parseStructuredNotes = ({
  notes,
  fallback,
}: IParseStructuredNotesParams): IStructuredNote[] | string => {
  const directNotes = parseStructuredNotesArray(notes);
  if (directNotes !== null) {
    return directNotes;
  }

  if (typeof notes === "string") {
    try {
      const parsed = JSON.parse(notes) as unknown;
      const parsedNotes = parseStructuredNotesArray(parsed);
      if (parsedNotes !== null) {
        return parsedNotes;
      }
      return notes;
    } catch {
      return notes;
    }
  }

  if (isRecord(notes)) {
    const nestedNotes = parseStructuredNotesArray(notes.notes);
    if (nestedNotes !== null) {
      return nestedNotes;
    }
  }

  return fallback;
};

// NOTE: NSAI-6211 — the single owner of the "start - end" label every notes
// surface renders. A summariser that could not anchor a topic to a transcript
// line leaves time_start / time_end empty (structures.clean_timestamp blanks
// anything that is not a real clock reading), and pasting those into a template
// produced the bare " - " the Notes tab showed as an empty "( - )". An
// un-anchorable topic now yields "", which every caller reads as "render no
// time at all", and a topic with only a start yields that start on its own
// rather than a dangling dash.
export const formatTimeRange = ({
  timeStart,
  timeEnd,
}: IFormatTimeRangeProps): string => {
  const start = convertTimeFormat(timeStart).trim();
  const end = convertTimeFormat(timeEnd).trim();

  if (!start) {
    return "";
  }

  return end ? `${start} - ${end}` : start;
};

// NOTE: NSAI-6211 — the reverse of formatTimeRange. Every notes surface receives
// the finished "start - end" label as a prop and has to read the ends back out
// of it: web and mobile to decide which controls to render, the notes list to
// decide whether a point can seek at all. Splitting it in three places is how
// web ended up rendering an empty "( - )" while mobile already returned null,
// so the split lives here, beside the function that produced the label. An end
// with no start yields no start — a range that cannot be seeked to.
export const splitTimeRange = (timeRange: string): ITimeRangeEnds => {
  const [rawStart, rawEnd] = timeRange.split(" - ");
  const start = rawStart?.trim() || "";

  if (!start) {
    return { start: "", end: "" };
  }

  return { start, end: rawEnd?.trim() || "" };
};

export const groupNotesByTheme = (
  notes: IStructuredNote[],
): IGroupedNotesByThemeItem[] => {
  const grouped = new Map<
    string,
    {
      emoji?: string;
      entries: Array<{ timeRange: string; points: string[] }>;
    }
  >();

  notes.forEach((note) => {
    const timeRange = formatTimeRange({
      timeStart: note.time_start,
      timeEnd: note.time_end,
    });
    const nextEntry = { timeRange, points: note.points };

    if (grouped.has(note.theme)) {
      grouped.get(note.theme)?.entries.push(nextEntry);
      return;
    }

    grouped.set(note.theme, {
      emoji: note.emoji,
      entries: [nextEntry],
    });
  });

  return Array.from(grouped.entries()).map(([theme, { emoji, entries }]) => ({
    theme,
    emoji,
    timeEntries: entries,
  }));
};

// NOTE: The pre-`ISimpleActionItem` action-item shape — sections of bullet
// items with a time range. Still present on older meetings, so both clients
// (and the meeting export) keep rendering it; shared so all three agree on
// which rows are well-formed enough to show.
export const parseLegacyActionItemSections = (
  value: unknown,
): ILegacyActionItemsSection[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    if (!isRecord(item)) {
      return [];
    }

    const section = item.section;
    const timeRange = item.timeRange;
    const items = item.items;

    if (
      typeof section !== "string" ||
      typeof timeRange !== "string" ||
      !Array.isArray(items)
    ) {
      return [];
    }

    const parsedItems = items.filter((entry): entry is string => {
      return typeof entry === "string";
    });

    return [{ section, timeRange, items: parsedItems }];
  });
};

export const isSimpleActionItems = (
  actionItems: unknown,
): actionItems is ISimpleActionItem[] => {
  if (!Array.isArray(actionItems) || actionItems.length === 0) {
    return false;
  }

  const firstItem = actionItems[0];
  if (!isRecord(firstItem)) {
    return false;
  }

  return (
    typeof firstItem.time_start === "string" &&
    typeof firstItem.time_end === "string" &&
    typeof firstItem.description === "string"
  );
};

export const getInitials = (speaker: string): string => {
  const trimmed = speaker.trim();
  if (!trimmed) {
    return "U";
  }

  const parts = trimmed.split(/\s+/).filter(Boolean);
  const initials = parts
    .slice(0, 2)
    .map((part) => part[0] || "")
    .join("");

  return initials.toUpperCase() || "U";
};

interface IBuildStreamUrlProps {
  baseUrl: string;
  meetingUuid: string;
  token: string;
}

export const buildAudioStreamUrl = ({
  baseUrl,
  meetingUuid,
  token,
}: IBuildStreamUrlProps): string => {
  return `${baseUrl}/vstr-audio/${meetingUuid}?token=${encodeURIComponent(token)}`;
};

export const buildVideoStreamUrl = ({
  baseUrl,
  meetingUuid,
  token,
}: IBuildStreamUrlProps): string => {
  return `${baseUrl}/vstr-video/${meetingUuid}?token=${encodeURIComponent(token)}`;
};

// NOTE: Shared "video is still processing" poll loop for the web player and the
// mobile video tab (NSAI-4859). A video reported as processing can be minutes
// (on-demand iOS MP4 transcode) or hours (deferred playback-WebM encode of an
// uploaded video) away, so both clients re-ask with exponential backoff up to
// VIDEO_PROCESSING_POLL_MAX_ELAPSED_MS instead of a short fixed window, and let
// the terminal verdict come from the server. Lives here because the schedule is
// platform-agnostic: only the state updates differ, and those arrive as
// callbacks. Returns the cancel function each caller wires to effect cleanup.
export const startVideoProcessingPoll = ({
  checkAvailability,
  onAvailable,
  onUnavailable,
}: IStartVideoProcessingPollParams): (() => void) => {
  let isCancelled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const startedAt = Date.now();
  let delayMs = VIDEO_PROCESSING_POLL_INITIAL_MS;

  const poll = async (): Promise<void> => {
    const availability = await checkAvailability();
    if (isCancelled) {
      return;
    }

    if (availability === EMediaAvailability.AVAILABLE) {
      onAvailable();
      return;
    }

    // NOTE: Still processing — give up once the elapsed budget is spent so a
    // dead encode cannot keep a client polling forever.
    const isBudgetSpent =
      Date.now() - startedAt >= VIDEO_PROCESSING_POLL_MAX_ELAPSED_MS;
    if (availability === EMediaAvailability.UNAVAILABLE || isBudgetSpent) {
      onUnavailable();
      return;
    }

    delayMs = Math.min(delayMs * 2, VIDEO_PROCESSING_POLL_MAX_MS);
    timeoutId = setTimeout(() => void poll(), delayMs);
  };

  timeoutId = setTimeout(() => void poll(), delayMs);

  return (): void => {
    isCancelled = true;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };
};

// NOTE: Playback-speed helpers shared by the web and mobile recording players
// (NSAI-3798). Both read the last-used rate back from storage, where the value
// is untrusted (hand-edited, or written by an older build with a different
// ladder), so every value entering player state passes through here first —
// handing an out-of-range rate to a media element is what produces
// unplayable-fast audio with no way back except a reload.
export const normalizePlaybackRate = (value: unknown): number => {
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;

  if (typeof parsed !== "number" || !Number.isFinite(parsed)) {
    return DEFAULT_PLAYBACK_RATE;
  }

  const matchedRate = PLAYBACK_RATE_OPTIONS.find((rate) => rate === parsed);

  return matchedRate ?? DEFAULT_PLAYBACK_RATE;
};

// NOTE: Label for the speed control ("1x", "1.5x"). Kept out of i18n on
// purpose: the number carries the meaning and the "x" multiplier suffix reads
// the same in every locale we ship, so translating it would only risk a
// mismatch between the pill and the menu entries.
export const formatPlaybackRateLabel = (rate: number): string => {
  return `${normalizePlaybackRate(rate)}x`;
};
